import { AIFeature, AIResult, Question, QuestionAnswerResult, User } from '@zocks-communications/client-sdk-js'
import { User as DashboardUser } from '../../../../../models/User'

export interface AIFeatureModel extends AIFeature {
  results: AIResult[]
}

export const chartColors = ['#640AF5', '#9E42D6', '#C7A4FF', '#6C40B6', '#1CCECE', '#9B5CFF', '#20B6D7', '#4071ED']

export const ONE_DAY_IN_SECONDS = 60 * 60 * 24
export const ONE_HOUR_IN_SECONDS = 60 * 60

export const toTwoDigits = (num: number) => {
  return num.toLocaleString('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false,
  })
}

export interface AIFeatureViewModel {
  aiFeature: AIFeatureModel
  aiFeatureAnswers: { [id: string]: AIResult[] }
}

export interface QuestionAnswerResultViewModel {
  aiFeatureName: string
  question: Question
  aiResult: AIResult[]
}

export const getUserDisplayName = (user?: User | DashboardUser, fallback: string = '') => {
  return user
    ? user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.name
        ? user.name
        : user.username
          ? user.username
          : user.email
            ? user.email
            : user.id
    : fallback
}

export const formatDate = (value: string) => {
  const date = new Date(value)
  return date.toLocaleTimeString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })
}

export const formatDateDDMMYYYY = (value: string) => {
  const date = new Date(value)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  })
}

export const formatDateDDMM = (value: string) => {
  const date = new Date(value)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
  })
}

export const formateDateWeekday = (value: string) => {
  const date = new Date(value)
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export const formatDateHM = (value: string) => {
  const date = new Date(value)
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })
}

export const formatTime = (createTimeInSeconds: number, ignoreHourWhenZero: boolean = false) => {
  const hours = toTwoDigits(Math.floor((createTimeInSeconds % ONE_DAY_IN_SECONDS) / 3600))
  const minutes = toTwoDigits(Math.floor((createTimeInSeconds % ONE_HOUR_IN_SECONDS) / 60))
  const seconds = toTwoDigits(Math.floor(createTimeInSeconds % 60))

  if (ignoreHourWhenZero && hours === '00') {
    return `${minutes}:${seconds}`
  }

  return `${hours}:${minutes}:${seconds}`
}

export const formatTimeHM = (createTimeInSeconds: number) => {
  const hours = Math.floor((createTimeInSeconds % ONE_DAY_IN_SECONDS) / 3600)
  const minutes = Math.floor((createTimeInSeconds % ONE_HOUR_IN_SECONDS) / 60)
  const seconds = Math.floor(createTimeInSeconds % 60)

  return `${hours ? `${toTwoDigits(hours)}h` : ''} ${!hours && !minutes ? `${toTwoDigits(seconds)} sec` : `${toTwoDigits(minutes)} min`}`
}

export const formatTimeHMS = (createTimeInSeconds: number) => {
  const hours = Math.floor((createTimeInSeconds % ONE_DAY_IN_SECONDS) / 3600)
  const minutes = Math.floor((createTimeInSeconds % ONE_HOUR_IN_SECONDS) / 60)
  const seconds = Math.floor(createTimeInSeconds % 60)

  return `${hours ? `${hours}h` : ''} ${!hours && !minutes ? `${seconds}s` : `${minutes}m ${seconds}s`}`
}

export const getAnswersMap = (aiFeature: AIFeatureModel) => {
  return aiFeature.results.reduce((acc: { [id: string]: AIResult[] }, result) => {
    const qaResult = result.result as QuestionAnswerResult
    if (Object.keys(acc).includes(qaResult.questionId)) {
      acc[qaResult.questionId].push(result)
    } else {
      acc[qaResult.questionId] = [result]
    }
    return acc
  }, {})
}

const RELATIVE_TIME_FORMAT = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

const RELATIVE_TIME_FORMAT_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 31536000],
  ['month', 2592000],
  ['week', 604800],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
]

export const timeAgo = (value: string) => {
  const date = new Date(value)
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000)

  for (const [unit, value] of RELATIVE_TIME_FORMAT_UNITS) {
    const amount = Math.floor(seconds / value)
    if (Math.abs(amount) >= 1) {
      return RELATIVE_TIME_FORMAT.format(-amount, unit)
    }
  }

  return 'just now'
}

export const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
